<!-- eslint-disable vue/prop-name-casing -->
<script setup>
import dayjs from 'dayjs';
import { orderBy } from 'lodash-es';
import HawkTable from '~/common/components/organisms/hawk-table/hawk-table.vue';
import { useCommonImports } from '~/common/composables/common-imports.composable.js';
import TerraProgressBar from '~/terra/components/barcode-scan/terra-progress-bar.vue';
import { useTerraStore } from '~/terra/store/terra.store.js';

const props = defineProps({
  data: {
    type: Object,
    default: () => {},
  },
  projects: {
    type: Array,
    default: () => [],
  },
  filename_options: {
    type: Object,
    default: () => {},
  },
  saveFile: {
    type: Function,
  },
});

const { $t, $services, route } = useCommonImports();
const terra_store = useTerraStore();

const state = reactive({
  selected_projects: [],
  is_exporting: false,
});

const columns = computed(() => ([
  {
    id: 'select',
    header: '',
    accessorKey: 'select',
    size: 5,
  },
  {
    id: 'name',
    header: $t('Block'),
    accessorKey: 'name',
  },
  {
    id: 'progress',
    header: $t('Progress'),
    accessorKey: 'progress',
  },
  {
    id: 'trackers',
    header: $t('Trackers'),
    accessorKey: 'trackers',
    columns: [
      {
        id: 'pending',
        header: $t('Pending'),
        accessorKey: 'pending',
        size: 120,
      },
      {
        id: 'inprogress',
        header: $t('In progress'),
        accessorKey: 'inprogress',
        size: 120,
      },
      {
        id: 'completed',
        header: $t('Completed'),
        accessorKey: 'completed',
        size: 120,
      },
    ],
  },
  {
    id: 'modules',
    header: $t('Modules'),
    accessorKey: 'modules',
    columns: [
      {
        id: 'total_modules_count',
        header: $t('Total'),
        accessorKey: 'total_modules_count',
        size: 100,
      },
      {
        id: 'num_scanned_modules',
        header: $t('Scanned'),
        accessorKey: 'num_scanned_modules',
        size: 100,
      },
      {
        id: 'modules_pending',
        header: $t('Pending'),
        accessorKey: 'modules_pending',
        size: 100,
      },
      {
        id: 'duplicates',
        header: $t('Duplicates'),
        accessorKey: 'duplicates',
        size: 100,
      },
    ],
  },
]));
const table_data = computed(() => Object.values(props.data).map(value => ({
  uid: value.uid,
  name: value.name,
  progress: getProgress(value),
  pending: value.pending || 0,
  inprogress: value.inprogress || 0,
  completed: value.completed || 0,
  total_modules_count: value.total_modules_count || 0,
  num_scanned_modules: value.num_scanned_modules || 0,
  modules_pending: (value.total_modules_count || 0) - (value.num_scanned_modules || 0),
  duplicates: value.duplicates || 0,
})));

function getProgress(row) {
  const { pending, inprogress, completed } = row;
  const total = pending + inprogress + completed;
  const percentage_completed = Math.floor((completed / total) * 100);
  const percentage_inprogress = Math.floor(((inprogress / total) * 100), 100);
  const percentage_pending = Math.floor(((pending / total) * 100), 100);
  return {
    progress_details: [
      { percentage: percentage_completed, color: '#12B76A', tooltip: `${percentage_completed}% Completed` },
      { percentage: percentage_inprogress + percentage_completed, color: '#FDB022', tooltip: `${percentage_inprogress}% In-progress` },
      { percentage: percentage_pending + percentage_inprogress + percentage_completed, color: '#F04438', tooltip: `${percentage_pending}% Pending` },
    ],
    percentage_completed,
  };
}

function handleRowSelection(e) {
  state.selected_projects = e.map(row => row.original);
}
function getFileName(type = '') {
  const filename_parts = [];
  filename_parts.push(props.filename_options.asset_name);
  if (type)
    filename_parts.push(type);
  if (state.selected_projects.length === 1)
    filename_parts.push(state.selected_projects[0].name);
  filename_parts.push(`${props.filename_options.current_date}.xlsx`);
  return filename_parts.join('_');
}

// Composable for handling notes and attachments export
function useNotesAndAttachmentsExport() {
  const fetchNotesAndAttachments = async (data) => {
    // Group features by project
    const project_features_map = data.reduce((acc, item) => {
      if (item.feature_uid && item.project_uid) {
        if (!acc[item.project_uid]) {
          acc[item.project_uid] = new Set();
        }
        acc[item.project_uid].add(item.feature_uid);
      }
      return acc;
    }, {});

    // Fetch notes metadata for all features grouped by project (one API call per project)
    const notes_promises = Object.entries(project_features_map).map(async ([project_uid, feature_uids_set]) => {
      try {
        const container_uid = route.params.id;
        const feature_uids = Array.from(feature_uids_set);
        const { data: metadata } = await $services.terra.post({
          url: `barcode-scans/container/${container_uid}/project/${project_uid}/metadata`,
          baseURL: import.meta.env.VITE_APP_API_HOST,
          body: { feature_uids },
        });
        return metadata || {};
      }
      catch (err) {
        logger.error(err);
        return {};
      }
    });

    const notes_results = await Promise.all(notes_promises);
    return notes_results.reduce((acc, metadata) => {
      return { ...acc, ...metadata };
    }, {});
  };

  const getSerialNumberKey = (moduleXY) => {
    if (!moduleXY)
      return '';
    const [x, y] = moduleXY.split(',').map(v => Number.parseInt(v.trim()));
    if (!Number.isNaN(x) && !Number.isNaN(y)) {
      return `${x - 1}:${y - 1}`;
    }
    return '';
  };

  const formatNotes = (notes) => {
    if (!notes || notes.length === 0)
      return '';
    return notes.map(note => note.message?.replace(/<[^>]*>/g, '').trim()).filter(Boolean).join('; ');
  };

  const formatAttachments = (attachments) => {
    if (!attachments || attachments.length === 0)
      return '';

    // Get URL for each attachment and return comma-separated URLs as hyperlink
    const urls = attachments.map((att) => {
      return att?.pre_signed_url
        || att?.service?.url
        || att?.url
        || att?.service?.location
        || '';
    }).filter(Boolean);

    if (urls.length === 0)
      return '';

    // Return as hyperlink object for Excel - show URLs as text with the first URL clickable
    return {
      text: urls.join(', '),
      hyperlink: urls[0],
    };
  };

  const formatDescription = (attachments) => {
    if (!attachments || attachments.length === 0)
      return '';

    // Get URL for each attachment and return comma-separated URLs as hyperlink
    const notes = attachments.map((att) => {
      return att?.note
        || att?.notes
        || '';
    }).filter(Boolean);

    if (notes.length === 0)
      return '';

    // Return as hyperlink object for Excel - show URLs as text with the first URL clickable
    return notes.join(', ');
  };

  return {
    fetchNotesAndAttachments,
    getSerialNumberKey,
    formatNotes,
    formatAttachments,
    formatDescription,
  };
}

async function downloadReport() {
  try {
    const extra_property_keys = terra_store.container?.properties?.barcode_scan_extra_properties || [];

    const { data } = await $services.terra.post({
      url: '/features/barcode/serial-numbers/',
      query: {
        json: true,
        pending: true,
      },
      body: {
        projects: state.selected_projects?.length ? state.selected_projects.map(item => item.uid) : props.projects,
        barcode_scan_extra_properties: extra_property_keys,
      },
    });

    const { fetchNotesAndAttachments, getSerialNumberKey, formatNotes, formatAttachments, formatDescription } = useNotesAndAttachmentsExport();
    const notes_map_by_feature = await fetchNotesAndAttachments(data);

    const export_data = data.map((item) => {
      const lat = item.latitude ?? item.lat ?? null;
      const lng = item.longitude ?? item.lng ?? null;
      const location_display = (lat != null && lng != null) ? `${lat}, ${lng}` : '';
      const location_link = (lat != null && lng != null) ? `https://www.google.com/maps?q=${encodeURIComponent(lat)},${encodeURIComponent(lng)}` : '';
      const ts = item.timestamp ?? item.scanned_at ?? item.created_at ?? item.duplicated_at ?? null;
      const timestamp_formatted = ts && dayjs(ts).isValid() ? dayjs(ts).format('DD MMM YYYY hh:mm A') : '';

      // Get notes and attachments for this cell
      const feature_notes_map = notes_map_by_feature[item.feature_uid] || {};
      const serial_number_key = getSerialNumberKey(item['module(x,y)']);
      const cell_data = feature_notes_map[serial_number_key] || {};

      // const feature_extra_properties = terra_store.features_hash?.[item.feature_uid]?.properties?.extraProperties || {};
      const extra_properties_columns = extra_property_keys.reduce((acc, key) => {
        const value = item[key];
        let formatted_value = '';
        if (value != null) {
          if (Array.isArray(value))
            formatted_value = value.join(', ');
          else if (typeof value === 'object')
            formatted_value = JSON.stringify(value);
          else
            formatted_value = value;
        }
        acc[key] = formatted_value;
        return acc;
      }, {});

      return {
        'Block': item.project_name,
        'Tracker/Table': item.feature_name,
        'Module number': item.module_number,
        'Serial number': item.serial_number,
        'Module(X,Y)': item['module(x,y)'],
        ...extra_properties_columns,
        'Indicator': item.has_indicator ? 'True' : 'False',
        'Duplicate': item.is_duplicate,
        'Duplicate At': item.duplicated_at,
        'GPS Coordinates': location_link ? { text: location_display, hyperlink: location_link } : '',
        'Timestamp': timestamp_formatted,
        'Notes': formatNotes(cell_data.notes),
        'Attachments': formatAttachments(cell_data.attachments),
        'Description': formatDescription(cell_data.attachments),
      };
    });
    await props.saveFile(orderBy(export_data, ['Block', 'Tracker/Table', 'Module number']), getFileName());
  }
  catch (error) {
    logger.log(error);
  }
  state.is_exporting = false;
}
async function downloadTableData() {
  const export_data = table_data.value.map(item => ({
    'Block': item.name,
    'Progress': item.progress.percentage_completed,
    'Trackers-Pending': item.pending,
    'Trackers-In progress': item.inprogress,
    'Trackers-Completed': item.completed,
    'Modules-Total': item.total_modules_count,
    'Modules-Scanned': item.num_scanned_modules,
    'Modules-pending': item.modules_pending,
    'Duplicates': item.duplicates,
  }));
  await props.saveFile(export_data, getFileName('summary'));
}
</script>

<template>
  <div class="py-3">
    <HawkExportToast
      v-if="state.is_exporting"
      :submit="downloadReport"
      :progress_text="$t('Exporting to XLSX')"
      :completed_text="$t('Exported XLSX')"
      @cancel="state.is_exporting = false"
      @close="state.is_exporting = false"
    />
    <div class="flex items-center justify-between py-3">
      <div class="text-lg font-semibold">
        {{ $t('Block wise report') }}
      </div>
      <div class="flex items-center gap-x-2">
        <HawkButton type="plain" icon @click="downloadTableData">
          <div v-tippy="{ content: $t('Download table'), placement: 'top' }">
            <IconHawkDownloadTwo />
          </div>
        </HawkButton>
        <hawk-button type="outlined" @click="state.is_exporting = true">
          {{ $t('Export data') }}
        </hawk-button>
      </div>
    </div>
    <div class="max-h-[60vh] w-full overflow-auto scrollbar border rounded-lg">
      <HawkTable
        is_gapless
        :pagination_config="{ pageSize: table_data.length }"
        :data="table_data"
        :columns="columns"
        :show_menu_header="false"
        :disable_resize="true"
        show_column_borders
        @select-row="handleRowSelection"
      >
        <template #progress="{ data: { row: { original: { progress } } } }">
          <div class="flex items-center">
            <div class="w-40">
              <TerraProgressBar :progress_details="progress.progress_details" />
            </div>
            <span class="text-sm font-medium ml-2">{{ progress.percentage_completed }}%</span>
          </div>
        </template>
      </HawkTable>
    </div>
  </div>
</template>
